import { PencilIcon } from "lucide-react";
import { useState } from "react";
import { SubmitHandler } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { useMutation } from "urql";

import { TrialForm } from "@/components/forms/TrialForm";
import { TrialInput, zTrialInput } from "@/components/forms/validation/trial";
import { buttonVariants } from "@/components/ui/button";
import { DestructiveConfirmationAlertDialog } from "@/components/ui/destructive-confirmation-alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { graphql } from "@/gen";
import { TrialT } from "@/routes/trial/queries";
import { cn } from "@/utils";

const updateTrialMutation = graphql(`
    mutation updateTrial($input: UpdateTrialInput!) {
        updateTrial(input: $input) {
            id
            ...useTrial_Trial
        }
    }
`);

const deleteTrialMutation = graphql(`
    mutation deleteTrial($trialId: UUID!) {
        deleteTrial(trialId: $trialId)
    }
`);

export const TrialEditDialog = () => {
    const [_res, updateTrial] = useMutation(updateTrialMutation);
    const { trial } = useOutletContext<{
        trial: TrialT;
    }>();

    const [_resDelete, deleteTrial] = useMutation(deleteTrialMutation);
    const [isOpen, setIsOpen] = useState(false);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState(false);

    const onSubmit: SubmitHandler<TrialInput> = async input => {
        if (!input.player1Id || !input.player2Id) {
            toast.error("Both players are required");
            return;
        }

        const result = await updateTrial({
            input: {
                id: trial.id,
                player1Id: input.player1Id,
                player2Id: input.player2Id,
                result: input.result,
                finishedAt: input.finishedAt ? new Date(input.finishedAt).toISOString() : null,
            },
        });

        if (result.error) {
            toast.error("Unable to update trial", { description: result.error.message });
            for (const error of result.error.graphQLErrors) {
                toast.error(error.message);
            }
        } else {
            toast.success("Trial was updated");
            setIsOpen(false);
        }
    };

    const handleDelete = async () => {
        const result = await deleteTrial({ trialId: trial.id });

        if (result.error) {
            toast.error("Unable to delete trial", {
                description: result.error.message,
            });
        } else {
            toast.success("Trial was deleted");
            setIsOpen(false);
        }
    };

    const onDelete = () => {
        setDeleteAlertOpen(true);
    };

    const defaultValues = zTrialInput.parse({
        ...trial,
        player1Id: trial.player1.id,
        player2Id: trial.player2.id,
    });

    return (
        <>
            <Dialog open={isOpen} onOpenChange={() => setIsOpen(!isOpen)}>
                <DialogTrigger className={cn(buttonVariants())}>
                    <PencilIcon />
                </DialogTrigger>
                <DialogContent
                    className="max-h-[80vh] w-full max-w-3xl overflow-y-auto"
                    aria-describedby={undefined}
                >
                    <DialogHeader>
                        <div className="mr-4 flex items-center justify-between">
                            <DialogTitle>Edit trial</DialogTitle>
                        </div>
                    </DialogHeader>
                    <TrialForm
                        onSubmit={onSubmit}
                        defaultValues={defaultValues}
                        onDelete={onDelete}
                    />
                </DialogContent>
            </Dialog>

            <DestructiveConfirmationAlertDialog
                open={deleteAlertOpen}
                onOpenChange={setDeleteAlertOpen}
                onConfirm={handleDelete}
                title="Delete Trial"
                description="Are you sure you want to delete this trial? This action cannot be undone."
            />
        </>
    );
};
